import { attr, belongsTo } from "@ember-data/model";
import type Store from "@ember-data/store";
import { service } from "@ember/service";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/abstract/planified-journal-event";
import type Patient from "parog-web/models/patient";
import tWithDefault from "parog-web/utils/t-with-default";
import type TreatmentHistoryChangedJournalEvent from "./treatment-history-changed-journal-event";

//---- Automatically generated, don't update this part ----

type ResponsibleTypeStringEnum = "medic" | "patient";

/**
 * Represents the planification of a consultation.
 * The date of the consultation is set in occurredAt, even if it's in the future.
 */
export default class ConsultationPlannedJournalEvent extends JournalEvent {
  static type = "consultation-planned-journal-event";

  /**
   * An OMOP provider.
   * The fields specialty-source-concept should be set to SPE (the only concept currently supported),
   * and specialty-source-value is a number representing the specialty.
   * @see provider.yml for more documentation.
   */
  @attr() declare provider: any;

  /**
   * An OMOP procedure.
   * The field procedure-source-concept should be set to SEMEIA as it comes from our software.
   * @see procedure-occurence.yml for more documentation.
   */
  @attr() declare procedure: any;

  /**
   * The uuid of the corresponding treatment when consultation generated from a treatment history journal event.
   */
  @attr("string") declare originConsultationIdentifier: string;

  /**
   * The type of the responsible. Could be null.
   */
  @attr("string") declare responsibleType: ResponsibleTypeStringEnum;

  /**
   * The id of the responsible, could be null
   */
  @attr("number") declare responsibleId: number;

  static validations(intl: IntlService) {
    return {
      procedure: validatePresence({
        presence: true,
        description: intl.t(
          "validations.consultation-planned-journal-event.procedure"
        )
      }),
      provider: validatePresence({
        presence: true,
        description: intl.t(
          "validations.consultation-planned-journal-event.provider"
        )
      }),
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.consultation-planned-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée plus de 50 ans en avance. Veuillez modifier la date",
          onOrBefore: dayjs().add(50, "years").toDate()
        })
      ],
      responsibleId: validateNumber({
        allowBlank: true,
        integer: true,
        description: intl.t(
          "validations.consultation-planned-journal-event.responsible-id"
        )
      }),
      responsibleType: validateInclusion({
        list: ["medic", "patient"],
        allowBlank: true,
        description: intl.t(
          "validations.consultation-planned-journal-event.responsible-type"
        )
      })
    };
  }

  //---- End of generated code ----

  @service declare store: Store;

  @belongsTo("patient", {
    async: false,
    inverse: "consultationPlannedJournalEvents"
  })
  declare patient: Patient;

  @belongsTo("treatment-history-changed-journal-event", {
    async: false,
    inverse: "consultationPlannedJournalEvents"
  })
  declare treatmentHistoryChangedJournalEvent: TreatmentHistoryChangedJournalEvent;

  get label(): string {
    return this.procedure.sourceLabel;
  }

  get responsibleFullName(): string | undefined {
    return this.store.peekRecord(this.responsibleType, this.responsibleId)
      ?.fullName;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "consultation-planned-journal-event": ConsultationPlannedJournalEvent;
  }
}
